import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { omit as _omit } from 'lodash';
import React, { useRef, useState } from 'react';
import type { FileRejection } from 'react-dropzone';

import { uploadAsset2MediaFile } from '../../../../apis/assetManager/assetManager';
import type { UploadError } from '../../../../apis/assetManager/types';
import { AssetErrorType } from '../../../../apis/assetManager/types';
import type { AssetType } from '../../../../configs';
import type { Asset2, Nullable } from '../../../../models';
import useGetAssetById from '../../../AdForm/CreativeBuilders/hooks/useGetAssetById';
import { INCOMPATIBLE_DIMENSIONS, INVALID_FILE_TYPE, VIDEO_WRONG_CODEC } from '../../../AssetUploader/const';
import { openToastAlert } from '../../../ToastAlert/toastAlert';
import { useFieldFast } from '../../hooks';

export type UseAssetUploadResult = {
  asset: Partial<Asset2>;
  setAsset: (value: Partial<Asset2>, shouldValidate?: boolean | undefined) => void;
  loading: boolean;
  percentUploaded?: number;
  onDrop: (acceptedFile: File, fileRejections: FileRejection[]) => Promise<void>;
  cancelRequest: () => void;
};

export const catchUploadError = (fileName: string, e: UploadError | Error): void => {
  let prompt = 'message' in e ? e.message : '';

  if ('type' in e && !prompt) {
    if (e.type === AssetErrorType.cancel) return;

    if (e.apiStatusCode === 400) {
      prompt = 'Please check the asset requirements and try again';
    } else {
      prompt = 'Please try again.';
    }
  }

  openToastAlert({
    alertType: 'error',
    message: 'Upload Failed',
    description: (
      <>
        Upload of <strong>{fileName}</strong> failed. {prompt}
      </>
    ),
  });
};

export const onDrop = async (
  acceptedFile: File,
  fileRejections: FileRejection[],
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setAsset: (value: Partial<Asset2>, shouldValidate?: boolean | undefined) => void,
  asset: Partial<Asset2> | undefined,
  assetType: AssetType,
  setPercentUploaded: React.Dispatch<React.SetStateAction<number | undefined>>,
  cancelTokenSourceRef: React.MutableRefObject<CancelTokenSource>,
  creativeLibraryId: string,
  fetchAssetById: (assetID: string) => Promise<Asset2>,
  lineItemCountry?: Nullable<string>
): Promise<void> => {
  if (fileRejections.length) {
    fileRejections.forEach((file) => {
      file.errors.forEach((err: { code: string; message: string }) => {
        if (err.code === INCOMPATIBLE_DIMENSIONS || err.code === INVALID_FILE_TYPE || err.code === VIDEO_WRONG_CODEC) {
          openToastAlert({
            alertType: 'error',
            message: err.code,
            description: <>{err.message}</>,
          });
        }
      });
    });
  } else {
    const file: File = acceptedFile;
    setLoading(true);
    // Need ability to display file name while loading
    setAsset({ ..._omit(asset, ['createdAt']), name: file.name });
    let caughtError = false;
    try {
      const assetModel: Asset2 = await uploadAsset2MediaFile({
        type: assetType,
        file,
        creativeId: asset?.id ?? null,
        creativeLibraryId,
        userId: null,
        mediaKey: file.name,
        setPercentUploaded,
        countryCode: lineItemCountry ?? null,
        cancelTokenSource: cancelTokenSourceRef.current,
      });

      const fetchedAsset = await fetchAssetById(assetModel.id);
      const review = fetchedAsset?.review ?? undefined;
      setAsset({
        ...assetModel,
        review: review,
      });
    } catch (e) {
      caughtError = true;
      catchUploadError(file.name, e as Error | UploadError);
    } finally {
      setLoading(false);
      !caughtError &&
        openToastAlert({
          alertType: 'success',
          message: 'Upload Succeeded',
          description: (
            <>
              <strong>{file.name}</strong> has been uploaded
            </>
          ),
        });
    }
  }
};

export default function useAssetUpload(
  formFieldName: string,
  assetType: AssetType,
  creativeLibraryId: string,
  lineItemCountry?: Nullable<string>,
  lineItemPublisherList?: string[]
): UseAssetUploadResult {
  const { fetchAssetById } = useGetAssetById(undefined, lineItemPublisherList);
  const [, { value: asset }, { setValue: setAsset }] = useFieldFast<Partial<Asset2> | undefined>(formFieldName);
  const [percentUploaded, setPercentUploaded] = useState<number>();
  const [loading, setLoading] = useState(false);

  // The CancelToken source needs to be managed in a ref so that it can be mutated by axios when requests are made
  // and so that any React components (like the "Cancel" button in the AssetUploader) has access to the current instance of the cancel function.
  const cancelTokenSourceRef = useRef<CancelTokenSource>(axios.CancelToken.source());

  function cancelRequest(): void {
    cancelTokenSourceRef.current.cancel();
    // reset cancelTokenSource for next request
    cancelTokenSourceRef.current = axios.CancelToken.source();
    setAsset(undefined);
  }

  const onDropProxy = async (acceptedFile: File, fileRejections: FileRejection[]): Promise<void> => {
    return onDrop(
      acceptedFile,
      fileRejections,
      setLoading,
      setAsset,
      asset,
      assetType,
      setPercentUploaded,
      cancelTokenSourceRef,
      creativeLibraryId,
      fetchAssetById,
      lineItemCountry
    );
  };

  const omittedAsset = { ..._omit(asset, ['createdAt', 'review', 'fileSize']), type: assetType };

  return {
    asset: omittedAsset,
    setAsset,
    loading,
    percentUploaded,
    onDrop: onDropProxy,
    cancelRequest,
  };
}
